import React from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import type { AgentSpecFormData } from '../hooks/useAgentSpecForm';

interface AgentIdentityProps {
  agentId: string;
  displayName: string;
  description: string;
  errors: Pick<Record<keyof AgentSpecFormData, string | null>, 'display_name' | 'description'>;
  retired: boolean;
  onChange: (field: 'display_name' | 'description', value: string) => void;
  /** Absent when this caller may not retire agents. */
  onRetire?: () => void;
  /** The default this agent's shape can hold; absent for a sub_agent or with authoring off. */
  defaultRole?: { role: 'investigation' | 'automation'; current: boolean; busy: boolean; onUse: () => void };
}

/**
 * Who the agent is: the id it keeps on the wire, and the name and description people read.
 *
 * Retire sits here because it acts on the row as a whole, and a retired row shows as such and
 * takes no edits (PS-UC-18).
 */
const AgentIdentity: React.FC<AgentIdentityProps> = ({
  agentId, displayName, description, errors, retired, onChange, onRetire, defaultRole,
}) => (
  <Box sx={{ px: 3, pt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
    <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
      <TextField
        label="Agent id"
        value={agentId}
        disabled
        helperText="Fixed once created"
        size="small"
        fullWidth
      />
    </Box>
    <Box sx={{ minWidth: 200, flex: '1 1 220px' }}>
      <TextField
        label="Display name"
        value={displayName}
        onChange={(e) => onChange('display_name', e.target.value)}
        disabled={retired}
        error={!!errors.display_name}
        helperText={errors.display_name ?? ' '}
        size="small"
        fullWidth
      />
    </Box>
    <Box sx={{ minWidth: 280, flex: '3 1 360px' }}>
      <TextField
        label="Description"
        value={description}
        onChange={(e) => onChange('description', e.target.value)}
        disabled={retired}
        error={!!errors.description}
        helperText={errors.description ?? ' '}
        size="small"
        fullWidth
      />
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
      {retired ? (
        <Chip label="Retired" size="small" />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {defaultRole && (defaultRole.current ? (
            <Chip label={`${defaultRole.role === 'investigation' ? 'Investigation' : 'Automation'} agent`} size="small" color="primary" />
          ) : (
            <Button size="small" variant="outlined" onClick={defaultRole.onUse} disabled={defaultRole.busy}>
              Use as {defaultRole.role} agent
            </Button>
          ))}
          {onRetire && (
            <Button
              size="small" color="error" variant="outlined" onClick={onRetire}
              // What a default runs would fail without it, so the server refuses it too.
              disabled={defaultRole?.current}
            >
              Retire
            </Button>
          )}
        </Box>
      )}
    </Box>
    {!retired && defaultRole?.current && onRetire && (
      <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', mt: -1 }}>
        This is the current {defaultRole.role} agent, so it cannot be retired. Make another agent the{' '}
        {defaultRole.role} agent first.
      </Typography>
    )}
  </Box>
);

export default AgentIdentity;
